import React, { useState } from 'react';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import { capitalize } from '@mui/material/utils';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { LoadingButton } from '@mui/lab';
import CardLoader from '@/components/CardLoader';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment';
import { useFormik, FormikProvider, Form } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { AMDLinkStyles } from './index';
import { DateInput } from '@/components/DateInput';

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  dob: Yup.string().required('Date of birth is required'),
  sex: Yup.string().required('Sex is required')
});

const errorMessages = {
  MISSING_CUSTOMER_GENDER: 'POW Client has no gender',
  MISSING_DOB: 'Date of birth is missing'
};

const SearchAdvancedMD = (props: any) => {
  const { customerData, patientId, setPatient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = AMDLinkStyles();

  const [createNewAMD, setCreateNewAMD] = useState(false);
  const [medicalApiStatus, setMedicalApiStatus] = useState(null);
  const [isCreatePatientModalOpen, setIsCreatePatientModalOpen] =
    useState(false);
  const [loadingCreatePatient, setLoadingCreatePatient] = useState(false);
  const [patientSearchResults, setPatientSearchResults] = useState([]);

  const initialValues = {
    firstName: customerData?.firstName || '',
    lastName: customerData?.lastName || '',
    dob: customerData?.dob
      ? moment.utc(customerData.dob).format('YYYY-MM-DD')
      : '',
    sex:
      customerData?.gender === 'None'
        ? 'undisclosed'
        : customerData?.gender?.toLowerCase() || ''
  };

  const handleCreatePatient = async () => {
    setLoadingCreatePatient(true);
    try {
      const linkCustomerId = customerData.id;
      const response = await formDataInstance.post(
        CONFIG.MEDICAL_API_URL + '/staff/patients/',
        {
          customerId: linkCustomerId
        },
        {
          headers: getRestHeaders()
        }
      );
      setPatient({
        ...response?.data?.data,
        linkedCustomerId: linkCustomerId
      });
      const { firstName, lastName, dob, sex } = response?.data?.data;
      formik.setValues({
        firstName,
        lastName,
        dob: moment.utc(dob).format('YYYY-MM-DD'),
        sex
      });
      enqueueSnackbar('Patient created and linked successfully', {
        variant: 'success'
      });
      formik.submitForm();
    } catch (e) {
      const msg =
        errorMessages[e?.response?.data?.error_code] ||
        'Error creating patient';
      enqueueSnackbar(msg, {
        variant: 'error'
      });
    } finally {
      setLoadingCreatePatient(false);
      setIsCreatePatientModalOpen(false);
    }
    setCreateNewAMD(true);
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
    onSubmit: async values => {
      setMedicalApiStatus('LOADING');
      if (createNewAMD) {
        setMedicalApiStatus('SUCCESS');
      } else {
        try {
          const response = await formDataInstance.get(
            CONFIG.MEDICAL_API_URL + '/staff/patient-search/',
            {
              params: {
                ...values,
                dob: new Date(values.dob),
                customerId: customerData.id
              },
              headers: getRestHeaders()
            }
          );
          setPatientSearchResults(response?.data?.data);
          setMedicalApiStatus('SUCCESS');
        } catch (error) {
          enqueueSnackbar('Error searching AdvancedMD', {
            variant: 'error'
          });
          if (error?.response?.data?.error_code === 'FAILED_FINDING_PATIENTS') {
            setMedicalApiStatus('FAILED');
          }
        }
      }
    }
  });

  return (
    <>
      <Box pt={2} pr={2} pl={2}>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
            <Typography className={classes.columnHeader}>
              Search AdvancedMD
            </Typography>
            <form noValidate autoComplete="off">
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="First name"
                  fullWidth
                  sx={{ mb: 1 }}
                  {...formik.getFieldProps('firstName')}
                />
                <TextField
                  label="Last name"
                  fullWidth
                  sx={{ mb: 1 }}
                  {...formik.getFieldProps('lastName')}
                />
                <DateInput
                  label="Date of birth"
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                  sx={{ mb: 1 }}
                  {...formik.getFieldProps('dob')}
                />
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel id="sex-label">Sex</InputLabel>
                  <Select
                    labelId="sex-label"
                    label="Sex"
                    defaultValue=""
                    {...formik.getFieldProps('sex')}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="undisclosed">Undisclosed</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </form>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {Object.keys(formik.errors).length > 0 && (
                <Grid item xs={12}>
                  <Typography className={classes.errorMessage}>
                    All fields are required
                  </Typography>
                </Grid>
              )}
              <Grid item xs={5}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  loading={formik.isSubmitting}
                  onClick={() => setCreateNewAMD(false)}
                >
                  Search AdvancedMD
                </LoadingButton>
              </Grid>
              <Grid item xs={7}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={async () => {
                    const isValid = await formik.validateForm();
                    if (Object.keys(isValid).length === 0) {
                      setIsCreatePatientModalOpen(true);
                    }
                  }}
                >
                  Create new AdvancedMD profile
                </Button>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </Box>
      {medicalApiStatus === 'SUCCESS' ? (
        patientSearchResults.length > 0 ? (
          <Box p={2} className={classes.columnSection} sx={{ padding: 0 }}>
            <List className={classes.searchResult} sx={{ padding: 0 }}>
              <Box
                style={{
                  overflow: 'auto',
                  height: 'calc(100vh - 500px)'
                }}
              >
                {patientSearchResults.map((patient, index) => (
                  <ListItem
                    key={index}
                    button
                    sx={{
                      padding: '8px 16px',
                      backgroundColor:
                        patient?.patientId === patientId ? '#f5f5f5' : 'inherit'
                    }}
                    onClick={() => setPatient(patient)}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ListItemText
                          primary={
                            <>
                              <Typography className={classes.searchText}>
                                {patient?.firstName} {patient?.lastName}
                              </Typography>
                              <Typography className={classes.searchText}>
                                DOB: {patient?.dob}
                              </Typography>
                              <Typography className={classes.searchText}>
                                Sex: {capitalize(patient?.sex ?? '')}
                              </Typography>
                              <Typography className={classes.searchText}>
                                {patient?.phone}
                              </Typography>
                              <Typography className={classes.searchText}>
                                {patient?.email?.toLowerCase()}
                              </Typography>
                            </>
                          }
                        />
                      </Grid>
                      <Grid item xs={6} container justifyContent="right">
                        {patient?.linkedCustomerId && (
                          <Typography className={classes.searchText}>
                            [
                            {patient?.linkedCustomerId === customerData.id
                              ? 'LINKED'
                              : 'CONFLICTING LINK'}
                            ]
                          </Typography>
                        )}
                        <ChevronRightIcon className={classes.searchChevron} />
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </Box>
            </List>
          </Box>
        ) : (
          <Box p={2} className={classes.columnSection}>
            <Typography className={classes.columnHeader}>
              No AdvancedMD profiles matching the search terms.
            </Typography>
          </Box>
        )
      ) : (
        <Box p={2} className={classes.columnSection}>
          {medicalApiStatus === null && (
            <Typography className={classes.lightText}>
              Use the search fields above to search the AdvancedMD client
              database.
            </Typography>
          )}
          {medicalApiStatus === 'LOADING' && <CardLoader />}
          {medicalApiStatus === 'FAILED' && (
            <Typography className={classes.columnHeader}>
              No AdvancedMD profiles matching the search terms.
            </Typography>
          )}
        </Box>
      )}
      <Dialog
        open={isCreatePatientModalOpen}
        onClose={() => setIsCreatePatientModalOpen(false)}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.dialogTitleContent}>
            <InfoIcon
              color={customerData?.linkStatus == 'linked' ? 'error' : 'primary'}
              style={{ marginRight: '8px' }}
            />
            <h3>Create new AdvancedMD profile</h3>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography className={classes.lightText}>
                Are you sure you want to create and link a new AdvancedMD
                profile with the current POW client's details?
                <b>
                  {customerData?.linkStatus == 'linked'
                    ? ' The current POW client has already been linked to an existing AdvancedMD profile.'
                    : ''}
                </b>
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <div className={classes.dialogButtons}>
          <Button
            variant="outlined"
            style={{ marginRight: '8px' }}
            onClick={() => setIsCreatePatientModalOpen(false)}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={loadingCreatePatient}
            onClick={() => handleCreatePatient()}
          >
            Create
          </LoadingButton>
        </div>
      </Dialog>
    </>
  );
};

export default SearchAdvancedMD;
